import React, { useState, useEffect } from 'react'
import { C_DARK } from './theme.js'
// PinPad + VibeToggle — used by BOTH the kiosk and the dashboard
// self-views (/timeclock/me, /sewing/me). They take the theme palette as
// a `C` prop defaulting to C_DARK, since every place they render is
// dark-locked (the light/dark toggle only applies inside the
// authenticated dashboard, which doesn't use these). Extracted from
// cutting-dashboard.jsx for the kiosk bundle split.
export function PinPad({ pin, onChange, onSubmit, onCancel, actionLabel, busy, notes, onNotes, showNotes, C = C_DARK }) {
  const friendly = { in: 'Clock In', out: 'Clock Out', lunch_out: 'Lunch Out', lunch_in: 'Back from Lunch' }
  // Auto-submit when 4 digits entered
  useEffect(() => { if (pin.length === 4) { /* let user confirm via button to allow notes */ } }, [pin])
  const press = (d) => { if (pin.length < 4) onChange(pin + d) }
  const back  = () => onChange(pin.slice(0, -1))
  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div style={{textAlign:'center',fontSize:14,color:C.muted,letterSpacing:'.08em',textTransform:'uppercase'}}>
        Enter PIN to {friendly[actionLabel] || actionLabel}
      </div>
      <div style={{display:'flex',justifyContent:'center',gap:10,fontFamily:"'JetBrains Mono',ui-monospace,monospace",fontSize:38,fontWeight:800,letterSpacing:'.3em'}}>
        {[0,1,2,3].map(i => (
          <span key={i} style={{display:'inline-block',width:50,height:60,lineHeight:'60px',textAlign:'center',background:C.deep,border:`1px solid ${C.border}`,borderRadius:6,color:pin[i]?C.cyan:C.muted}}>
            {pin[i] ? '•' : '_'}
          </span>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3, 1fr)',gap:8,maxWidth:260,margin:'0 auto',width:'100%'}}>
        {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((d, i) => {
          if (d === '') return <div key={i}/>
          const isBack = d === '⌫'
          return (
            <button key={i} onClick={() => isBack ? back() : press(d)}
                    style={{padding:'18px 0',fontSize:24,fontWeight:700,background:isBack?'transparent':C.deep,color:isBack?C.muted:C.text,border:`1px solid ${C.border}`,borderRadius:6,cursor:'pointer',fontFamily:'inherit'}}>
              {d}
            </button>
          )
        })}
      </div>
      {showNotes && (
        <input value={notes} onChange={e=>onNotes(e.target.value)}
               placeholder="Note (e.g. forgot to clock in this morning) — optional"
               style={{padding:'10px 12px',fontSize:13,background:C.deep,border:`1px solid ${C.border}`,borderRadius:5,color:C.text,outline:'none',fontFamily:'inherit'}}/>
      )}
      <div style={{display:'flex',gap:8}}>
        <button onClick={onCancel} disabled={busy}
                style={{flex:1,padding:'12px 16px',fontSize:14,fontWeight:700,background:'transparent',color:C.muted,border:`1px solid ${C.border}`,borderRadius:5,cursor:'pointer',fontFamily:'inherit'}}>
          Cancel
        </button>
        {/* onClick wraps onSubmit() so the contract is "no args" — without
            the wrapper, React passes the SyntheticEvent (with a fiber-
            backed button DOM ref) as the first arg, and any caller that
            JSON.stringify's that arg blows up with a circular-structure
            error. Bit one of the operators on a clock-in (2026-05-07);
            the wrapper is now load-bearing.  */}
        <button onClick={() => onSubmit()} disabled={busy || pin.length !== 4}
                style={{flex:2,padding:'12px 16px',fontSize:14,fontWeight:800,letterSpacing:'.08em',textTransform:'uppercase',background: pin.length===4&&!busy ? C.cyan : 'transparent',color: pin.length===4&&!busy ? C.cyanText : C.muted,border:`1px solid ${pin.length===4&&!busy ? C.cyan : C.border}`,borderRadius:5,cursor: pin.length===4&&!busy ? 'pointer' : 'not-allowed',fontFamily:'inherit'}}>
          {busy ? 'Sending…' : 'Confirm'}
        </button>
      </div>
    </div>
  )
}

// Three-way vibe picker. PIN-gated server endpoint so this works from
// the kiosk and self-view without a dashboard login. Optimistic update
// with rollback on error.
//
// Modes:
//   neutral — standard professional copy (default)
//   snarky  — sassy + pranks + coworker gossip
//   nice    — encouragements + suggestions to be kind to others
export function VibeToggle({ operatorId, pin, initial, onChange, C = C_DARK }) {
  const [vibe, setVibe] = useState(initial || 'neutral')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Pending confirmation when switching INTO snarky — heads-up that
  // coworkers get notified about their tardiness/long lunches.
  const [pendingSnarky, setPendingSnarky] = useState(false)
  // After a snarky toggle, server tells us how many other people are
  // also in snarky mode. If 0, gossip silently won't fire — surface
  // that so the user isn't confused.
  const [loneSnark, setLoneSnark] = useState(false)

  const commit = async (next) => {
    const prev = vibe
    setVibe(next); setBusy(true); setError(null)
    try {
      const r = await fetch('/api/timeclock/lookup/vibe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operatorId, pin, vibeMode: next }),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      // Snarky-mode hint: are you the only one?
      setLoneSnark(next === 'snarky' && d.snarkyTeammateCount === 0)
      onChange?.(next)
    } catch (e) {
      setVibe(prev)
      setError(e.message)
    } finally { setBusy(false) }
  }
  const pick = (next) => {
    if (next === vibe) return
    // Switching INTO snarky? Show the heads-up first.
    if (next === 'snarky' && vibe !== 'snarky') {
      setPendingSnarky(true)
      return
    }
    commit(next)
  }
  const OPTIONS = [
    { v: 'neutral', label: 'Neutral', icon: '○',  desc: 'Standard professional copy.', color: C.muted },
    { v: 'snarky',  label: 'Snarky',  icon: '🔥', desc: 'Sass, pranks, gossip about coworkers.', color: '#a78bfa' },
    { v: 'nice',    label: 'Nice',    icon: '💛', desc: 'Encouragement + ideas for being kind.', color: '#fbbf24' },
  ]
  const current = OPTIONS.find(o => o.v === vibe) || OPTIONS[0]
  return (
    <div style={{padding:'12px 14px',background:C.surface,border:`1px solid ${vibe==='neutral'?C.border:current.color+'66'}`,borderRadius:8,marginBottom:10}}>
      <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:6,display:'flex',alignItems:'baseline',gap:8}}>
        <span>Time-clock vibe</span>
        <span style={{fontSize:11,fontWeight:500,color:C.muted,fontStyle:'italic'}}>{current.desc}</span>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3, 1fr)',gap:6}}>
        {OPTIONS.map(o => {
          const active = o.v === vibe
          return (
            <button key={o.v} onClick={()=>pick(o.v)} disabled={busy}
                    style={{
                      padding:'10px 6px', fontSize:12, fontWeight:700,
                      letterSpacing:'.06em', textTransform:'uppercase',
                      background: active ? `${o.color}22` : 'transparent',
                      color:      active ? o.color : C.muted,
                      border:    `1px solid ${active ? o.color+'88' : C.border}`,
                      borderRadius:5, cursor: busy?'wait':'pointer', fontFamily:'inherit',
                    }}>
                <span style={{fontSize:14,marginRight:4}}>{o.icon}</span>{o.label}
            </button>
          )
        })}
      </div>
      {error && <div style={{fontSize:11,color:'#fca5a5',marginTop:6}}>{error}</div>}
      {loneSnark && vibe === 'snarky' && (
        <div style={{marginTop:8,padding:'8px 12px',fontSize:11,lineHeight:1.5,background:'rgba(251,191,36,.08)',border:'1px solid rgba(251,191,36,.35)',color:'#fbbf24',borderRadius:5}}>
          🦗 You're the only one in snarky mode right now — coworker gossip won't fire until at least one teammate joins you. (Pranks, levels, and confusion bits still work.)
        </div>
      )}
      {pendingSnarky && (
        <div onClick={()=>setPendingSnarky(false)}
             style={{position:'fixed',inset:0,background:'rgba(0,0,0,.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:300,padding:20}}>
          <div onClick={e=>e.stopPropagation()}
               style={{background:C.surface,border:'1px solid rgba(167,139,250,.5)',borderRadius:10,width:'min(420px, 96vw)',padding:'18px 20px'}}>
            <div style={{fontFamily:"'Orbitron','Rajdhani',sans-serif",fontSize:18,fontWeight:800,color:'#a78bfa',letterSpacing:'.04em',marginBottom:10}}>
              🔥 Heads up
            </div>
            <div style={{fontSize:14,color:C.text,lineHeight:1.55,marginBottom:16}}>
              In snarky mode, your coworkers (who are also in snarky mode) might be notified if you're late or not back from lunch on time. It's all in fun, but if you'd rather stay private, pick Neutral or Nice instead.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <button onClick={()=>setPendingSnarky(false)} disabled={busy}
                      style={{padding:'10px 14px',fontSize:12,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',background:'transparent',color:C.muted,border:`1px solid ${C.border}`,borderRadius:5,cursor:'pointer',fontFamily:'inherit'}}>
                Cancel
              </button>
              <button onClick={()=>{ setPendingSnarky(false); commit('snarky') }} disabled={busy}
                      style={{padding:'10px 14px',fontSize:12,fontWeight:800,letterSpacing:'.08em',textTransform:'uppercase',background:'rgba(167,139,250,.18)',color:'#a78bfa',border:'1px solid rgba(167,139,250,.55)',borderRadius:5,cursor:'pointer',fontFamily:'inherit'}}>
                Bring on the sass
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
